import { mmFetchWithResponse } from 'api/mmFetchWithResponse';
import dayjs from 'utils/dayjsConfig';

const FC_TRANSFERS_SEARCH_PATH = 'api/inventory/v1/fc-transfers';

/**
 * Maps UI / route `transferId` to a positive integer path segment for `GET .../fc-transfers/{transferId}`.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function mapTransferIdToApiPathParam(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        return value;
    }
    const s = String(value).trim();
    if (!s) return undefined;
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n <= 0 || String(n) !== s) {
        return undefined;
    }
    return n;
}

/** @type {readonly string[]} */
const ALLOWED_SORT_FIELDS = Object.freeze(['id', 'createdAt', 'estimatedDepartureDate']);

/** @type {readonly string[]} */
const ALLOWED_SORT_DIRS = Object.freeze(['asc', 'desc']);

/**
 * Normalizes a single-day filter value to `YYYY-MM-DD` for inventory FC transfer search query params.
 * Accepts a plain date string or `{ date: Date | string }` from shared filter transformers (same shapes as `createdOn` / `estimatedDepartureDate`).
 *
 * @param {unknown} value
 * @returns {string | undefined}
 */
function normalizeSingleDayFilterToIsoDate(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'string') {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }
    if (typeof value === 'object' && value !== null && 'date' in value) {
        const d = /** @type {{ date?: Date | string }} */ (value).date;
        if (!d) return undefined;
        const parsed = dayjs(d);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }
    return undefined;
}

/**
 * Parses Transfer # filter into numeric `id` for exact-match search only.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
function parseTransferIdFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const s = String(value).trim();
    if (!s) return undefined;
    const n = parseInt(s, 10);
    if (Number.isNaN(n) || String(n) !== s) {
        return undefined;
    }
    return n;
}

/**
 * Builds URLSearchParams for `GET /inventory/v1/fc-transfers`.
 *
 * **Direction (Outbound / Inbound tabs):** The backend does not take a `direction` query param.
 * Tab choice is encoded by which facility filter is set: Outbound sends `sourceFacilityId` = logged-in
 * facility; Inbound sends `destinationFacilityId` = logged-in facility (see
 * `buildRequiredFCTransferListFilters`). Merged filter objects may still include `direction` for URL/UI
 * state — it is intentionally not serialized here.
 *
 * @param {Object} [params={}]
 * @returns {URLSearchParams}
 */
export function buildFCTransferSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.pageSize !== undefined && params.pageSize !== null && params.pageSize !== '') {
        queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.cursor !== undefined && params.cursor !== null && params.cursor !== '') {
        queryParams.set('cursor', String(params.cursor));
    }

    // Outbound: current facility as source. Inbound: current facility as destination. Not `direction=`.
    if (params.sourceFacilityId !== undefined && params.sourceFacilityId !== null && params.sourceFacilityId !== '') {
        queryParams.set('sourceFacilityId', String(params.sourceFacilityId));
    }
    if (
        params.destinationFacilityId !== undefined &&
        params.destinationFacilityId !== null &&
        params.destinationFacilityId !== ''
    ) {
        queryParams.set('destinationFacilityId', String(params.destinationFacilityId));
    }

    const transferId = parseTransferIdFilter(params.transferNumber);
    if (transferId !== undefined) {
        queryParams.set('id', String(transferId));
    }

    const createdAt = normalizeSingleDayFilterToIsoDate(params.createdOn);
    if (createdAt) {
        queryParams.set('createdAt', createdAt);
    }

    const estimatedDepartureDate = normalizeSingleDayFilterToIsoDate(params.estimatedDepartureDate);
    if (estimatedDepartureDate) {
        queryParams.set('estimatedDepartureDate', estimatedDepartureDate);
    }

    // TODO: backend will accept multiple statuses; until then send first selection only.
    if (Array.isArray(params.statuses) && params.statuses.length) {
        const first = params.statuses.find((s) => s !== undefined && s !== null && s !== '');
        if (first !== undefined) {
            queryParams.set('status', String(first));
        }
    }

    // TODO: backend will accept created-by identity filters; omit `createdByUserIds` until contract lands.
    // TODO: backend FC multi-select filter contract TBD; omit `fcIds` until confirmed.

    const { sortField } = params;
    const { sortDir } = params;
    if (
        sortField &&
        sortDir &&
        ALLOWED_SORT_FIELDS.includes(sortField) &&
        ALLOWED_SORT_DIRS.includes(String(sortDir).toLowerCase())
    ) {
        queryParams.set('sortField', sortField);
        queryParams.set('sortDir', String(sortDir).toLowerCase());
    }

    return queryParams;
}

/**
 * @param {Object} [params={}]
 * @returns {string}
 */
export function buildFCTransferSearchQueryString(params = {}) {
    return buildFCTransferSearchParams(params).toString();
}

/**
 * Search FC transfers (cursor-based list).
 *
 * @param {Object} [params={}] - Search filters and pagination
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {rows: Array, pagination: Object}}>}
 */
export async function searchFCTransfers(params = {}, options = {}) {
    const qs = buildFCTransferSearchQueryString(params);
    const url = qs.length > 0 ? `${FC_TRANSFERS_SEARCH_PATH}?${qs}` : FC_TRANSFERS_SEARCH_PATH;
    return mmFetchWithResponse(url, options);
}

/**
 * Fetches a single FC transfer with line items (`data.transfer` from API).
 *
 * @param {string | number} transferId - Transfer primary key (must normalize to a positive integer)
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer: Object}, statusCode?: number}>}
 */
export async function getFCTransferById(transferId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}`;
    return mmFetchWithResponse(url, options);
}

const FC_TRANSFERS_TRANSFER_INVENTORY_PATH = `${FC_TRANSFERS_SEARCH_PATH}/transfer-inventory`;

/**
 * Fetches lots/locations eligible for transfer assembly at a source facility (`data.transferInventory`).
 *
 * @param {Object} [params={}]
 * @param {number | string} [params.facilityId] - Source facility id (required)
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transferInventory?: Array}, statusCode?: number | null}>}
 */
export async function searchAvailableTransferLots(params = {}, options = {}) {
    const { facilityId } = params;
    if (facilityId === undefined || facilityId === null || facilityId === '') {
        return Promise.resolve({
            status: false,
            msg: 'facilityId is required',
            data: {},
            statusCode: null,
        });
    }
    const queryParams = new URLSearchParams();
    queryParams.set('facilityId', String(facilityId));
    const url = `${FC_TRANSFERS_TRANSFER_INVENTORY_PATH}?${queryParams.toString()}`;
    return mmFetchWithResponse(url, options);
}

/**
 * Creates a pending FC transfer (`data.transfer` on success).
 * Body matches inventory v1 contract: `sourceFacilityId`, `destinationFacilityId`,
 * `estimatedDepartureDate` (YYYY-MM-DD), `items` (`lotId`, `locationId`, `numCases`, `numLooseUnits`), optional `notes`.
 *
 * @param {Object} payload - JSON body for `POST /inventory/v1/fc-transfers`
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`); do not pass `body`/`method`
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function createFCTransfer(payload, options = {}) {
    return mmFetchWithResponse(FC_TRANSFERS_SEARCH_PATH, {
        ...options,
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
    });
}

/**
 * Adds one line item to a pending FC transfer (`POST .../fc-transfers/{transferId}/item`).
 * Backend requires transfer status `PENDING`.
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {Object} item - `{ lotId, locationId, numCases, numLooseUnits }` (inventory v1 add-item contract)
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function addFCTransferLineItem(transferId, item, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/item`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'POST',
        body: JSON.stringify({ item: item ?? {} }),
    });
}

/**
 * Removes a line item from a pending FC transfer (`DELETE .../fc-transfers/{transferId}/item/{transferItemId}`).
 * Backend requires transfer status `PENDING`.
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {string | number} transferItemId - Transfer line-item row id
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function removeFCTransferLineItem(transferId, transferItemId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    const itemId = mapTransferIdToApiPathParam(transferItemId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    if (itemId === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer item id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/item/${itemId}`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'DELETE',
    });
}

/**
 * Cancels a pending FC transfer (`PUT .../fc-transfers/{transferId}/cancel`).
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function cancelFCTransfer(transferId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/cancel`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'PUT',
    });
}
